import { EAppSupportType } from "./app-support-type";
import { BackEndValueType } from "./value-type";

export interface IKType {
  id: number;
  name: string;
  elements?: IKTypeField[];
  /**
   * Provenance of the type. Absent/undefined = datasource-derived (historical default).
   * "user" = a user-authored entry in the project-wide Kuika Data Types catalog.
   * Mirrors C# TypeModel.Origin.
   */
  origin?: "user" | "datasource";
}

export interface IKTypeField {
  name: string;
  prefix?: string;
  type: IKType;
  isList?: boolean;
  elementTypeModel?: IKType;
  elementResultActionId?: string;
}

/**
 * Canonical shape of a Kuika Data Types catalog entry — mirrors the C# `TypeModel`
 * (System.Text.Json camelCase names). Distinct from {@link IKType}: here a field's `type`
 * is a STRING (the element type name), with nested objects described by `elementTypeModel`.
 * The catalog speaks this shape end-to-end; convert to/from {@link IKType} only at the
 * designer boundary (symbol picker, datasource import).
 */
export interface IKuikaDataType {
  name: string;
  origin?: "user" | "datasource";
  mapping?: string;
  primaryKeyName?: string;
  typeSystemIdentifier?: string;
  isEnum?: boolean;
  elements?: IKuikaDataTypeField[];
  enumMembers?: { name: string; value?: string }[];
}

/** Field of a {@link IKuikaDataType} — mirrors C# `TypeElementModel`. */
export interface IKuikaDataTypeField {
  /** Element type NAME (scalar type or another catalog type's name). Not a nested object. */
  type: string;
  name: string;
  isList?: boolean;
  isEnum?: boolean;
  prefix?: string;
  typeSystemIdentifier?: string;
  /** Present when the field is itself a nested object rather than a scalar/named type. */
  elementTypeModel?: IKuikaDataType;
  allowedValues?: string[];
}

interface IInputOutput {
  id: string;
  name: string;
  type: string; // IKType;
  cardinality: ECardinality;
  description?: string;
}

export type IScreenInput = IInputOutput;

export type IScreenOutput = IInputOutput;

export interface IMethodInput extends IInputOutput {
  // Nested object support (mirrors ActionDefinitionParameterModel)
  Typename?: string;
  parameters?: IMethodInput[];
  isObject?: boolean;
  objectClassName?: string;
  ownerClassName?: string;
  ownerPropName?: string;
  realPropName?: string;
  displayName?: string;
  isMandatory?: boolean;
  isOptionalParameter?: boolean;
}

export type IMethodOutput = IInputOutput;

export type ICharacteristicFunctionInput = IInputOutput;

export type ICharacteristicFunctionOutput = IInputOutput;

export interface CalendarOptions {
  datavaluefield?: string;
  datatextfield?: string;
  datastartfield?: string;
  dataendfield?: string;
  datacolorfield?: string;
  dataheightfield?: string;
  datafontcolorfield?: string;
  datafontfamilyfield?: string;
  datafontsizefield?: string;
  datafontweightfield?: string;
  dataiconfield?: string;
  dataavatarfield?: string;
  // Static config (not a data field): max avatars shown before an Avatar.Group "+N" chip.
  // Carried through the same string-based field pipeline; coerced to a number at runtime.
  avatarMaxCount?: string;
}

export interface MenuOptions {
  dataidfield?: string;
  datatitlefield?: string;
  dataparentidfield?: string;
  datalinkfield?: string;
  dataiconfield?: string;
  dataorderfield?: string;
  databackgroundcolorfield?: string;
}

export interface BreadcrumbOptions {
  dataidfield?: string;
  datatitlefield?: string;
  dataparentidfield?: string;
  datalinkfield?: string;
  dataiconfield?: string;
  dataorderfield?: string;
}

export interface RadialChartOptions {
  dataslicetextfield?: string;
  dataslicelabelfield?: string;
  dataslicepointfield?: string;
  dataslicepercentagefield?: string;
}

export interface RadialChartLevelOptions {
  datalevelpercentagefield?: string;
  datalevelcolorfield?: string;
  dataleveltextfield?: string;
}

export interface MixedChartOptions {
  dataxaxisesfield?: string;
}

export interface MixedChartLineOptions {
  dataxaxisvaluefield?: string;
  dataxaxisfield?: string;
  dataxaxistooltipfield?: string;
  datavaluetooltipfield?: string;
}

export interface MixedChartPointOptions {
  dataxaxisfield?: string;
  datavaluefield?: string;
  dataxaxistooltipfield?: string;
  datavaluetooltipfield?: string;
}

export interface MixedChartBarOptions {
  dataxaxisfield?: string;
  datavaluefield?: string;
  dataxaxistooltipfield?: string;
  datavaluetooltipfield?: string;
}

export interface TimelineOptions {
  datatitlefield?: string;
  datadescriptionfield?: string;
  datacolorfield?: string;
}

export interface ChatOptions {
  chatId?: string;
  chatMessage?: string;
  chatUserName?: string;
  chatUserAvatar?: string;
  chatUserDisplayName?: string;
  chatDate?: string;
}

export interface KanbanBoardLaneOptions {
  laneId?: string;
  laneTitle?: string;
  laneLabel?: string;
  laneDroppable?: string;
  laneIndex?: string;
}

export interface KanbanBoardCardOptions {
  cardId?: string;
  cardLaneId?: string;
  cardTitle?: string;
  cardLabel?: string;
  cardSubTitle?: string;
  cardDescription?: string;
  cardEscalationText?: string;
  cardDraggable?: string;
  cardIndex?: string;
  cardSortKey?: string;
}

export interface NodeTreeNodeOptions {
  nodeTreeNodeId?: string;
  nodeTreeNodeParentId?: string;
  nodeTreeNodeDroppable?: string;
  nodeTreeConnectionHeight?: string;
  nodeTreeNodeWidth?: string;
  nodeTreeNodeHeight?: string;
  nodeTreeNodeBackgroundColor?: string;
  nodeTreeCompactChildView?: string;
  nodeTreeCrossLinkId?: string;
  nodeTreeCrossLinkWidth?: string;
  nodeTreeCrossLinkColor?: string;
}

export enum ECardinality {
  Unknown = 0,
  One = 1,
  Many = 2,
  LikeFirstParam = 3,
  Error = 4,
  Unbound = 5,
  Any = 6
}

export enum ECardinalityConversion {
  first = 0,
  last = 1,
  selected = 2,
  all = 3,
  self = 4,
  asArray = 5
}

export enum ESymbolCategory {
  none = 0,
  displayItem = 1,
  actionField = 2,
  inheritedAction = 3,
  fixed = 4,
  default = 5,
  screenInput = 6,
  // tableSelectedRowFieldConversion = 7,
  // FunctionResult = 8,
  actionResult = 9,
  actionResults = 10,
  bag = 11,
  current = 12,
  icons = 13,
  uploadedImages = 14,
  components = 15,
  screenInputs = 16,
  bags = 17,
  all = 18,
  fieldOfType = 19,
  cardinalityConvertions = 20,
  cardinalityConvertion = 21,
  typeConvertions = 22,
  typeConvertion = 23,
  displayItemProp = 24,
  processParameter = 25,
  inputMethod = 26,
  roleList = 27,
  workFlowStep = 28,
  masterScreen = 29,
  workFlowBOParameters = 30,
  workFlowInternalParameters = 31,
  workFlowInstanceParameters = 32,
  bluetoothValue = 33,
  sqlParameter = 34,
  postData = 35,
  // 36 is FunctionParameter on the C# side (Kuika.Common/Enums/ESymbolCategory.cs); skipped to keep the two
  // enums numerically aligned.
  /** A value taken from a declared document input (KOA-17254). Leaf, carries `inputName`. */
  documentParameterInput = 37,
  /** The group node holding the declared document inputs. */
  documentParameterInputs = 38,
  /**
   * A conditional parameter value (KOA-17739): `condition ? trueSymbol : falseSymbol`, carried in
   * `conditionalValue`. Honoured by the web (React) generator; other targets fall back to the
   * else branch (`falseSymbol`) until they support it.
   */
  condition = 39
}

/**
 * KOA-18057: one chart data series beyond the fixed first/second/third slots. Field
 * names mirror the designer symbol keys. `bgColorSourceType` selects whether
 * `bgColor` is a datasource column ("column"/legacy) or a literal color ("fixed").
 */
export interface ChartAdditionalSeries {
  dataField?: string;
  bgColor?: string;
  bgColorSourceType?: string;
  legendLabel?: string;
  lineStyle?: string;
}

/**
 * KOA-17739: payload of an `ESymbolCategory.condition` symbol — a parameter value resolved as
 * `condition ? trueSymbol : falseSymbol`. All fields are optional so half-configured states
 * survive editing; codegen falls back to `falseSymbol` when the condition has no rows.
 */
export interface IConditionalValue {
  condition?: IConditionModel;
  trueSymbol?: ISymbol;
  falseSymbol?: ISymbol;
}

export interface ISymbol {
  category: ESymbolCategory;
  label: string;
  description: string;

  cardinality?: ECardinality;
  currentType?: BackEndValueType;
  currentCardinality?: ECardinality;
  converterOfType?: string | undefined;
  converterOfCardinality?: ECardinalityConversion;
  targetType?: BackEndValueType;
  targetCardinality?: ECardinality;

  actionDefinitionId?: string | undefined; /// /Seçili olan action resultın meta data id..
  actionDefinitionObjectPath?: string | undefined; /// /Seçili olan action resultın path'i
  fieldName?: any;
  fieldType?: any;

  bagName?: string | undefined;

  isFixed?: boolean | undefined;
  fixedValue?: any | undefined;

  /** KOA-17739: set only when `category === ESymbolCategory.condition`. */
  conditionalValue?: IConditionalValue;

  inputName?: string | undefined;

  defaultValueName?: string | undefined;

  diId?: number | undefined; //* *** */
  diPropName?: any;

  fieldToDisplay?: string;
  fieldToUseAsKey?: string;
  fieldToLatitude?: string;
  fieldToLongitude?: string;
  fieldToUseAsParent?: string;
  fieldToUseAsChecked?: string;
  fieldIsCheckboxDisabled?: string;
  // KOA-18299 — optional Options-binding fields for SelectBox/MultiSelect. Both are additive and
  // absent on every screen serialized before this change, which is what keeps the flat, ungrouped
  // rendering byte-identical for existing apps.
  fieldToUseAsGroup?: string;
  fieldToUseAsTagColor?: string;
  // KOA-13797: Tree per-node bindings on the options symbol — icon (Material icon name or
  // image URL), colour, and the extra display columns rendered after the node text
  // (comma-joined field names, like `chartMultiTooltipContentField`). Additive and
  // optional — symbols without them behave exactly as before.
  fieldToUseAsIcon?: string;
  fieldToUseAsColor?: string;
  fieldsToUseAsColumns?: string;
  tableLabelSourceType?: string;

  chartDataLabel?: string;
  chartTooltipFooterDataLabel?: string;
  chartDataDisplayLabel?: string;
  chartFirstDataField?: string;
  chartFirstDataBgColor?: string;
  chartSecondDataField?: string;
  chartSecondDataBgColor?: string;
  chartThirdDataField?: string;
  chartThirdDataBgColor?: string;
  chartTargetLineField?: string;
  chartMultiTooltipContentField?: string;
  // Per-series source for the bg color: "column" (bind to a datasource column,
  // default/legacy) or "fixed" (use the literal color stored in the matching
  // chart*DataBgColor field). Lets a chart color be a constant, not a column.
  chartFirstDataBgColorSourceType?: string;
  chartSecondDataBgColorSourceType?: string;
  chartThirdDataBgColorSourceType?: string;
  // Optional fixed series (legend) label. When empty the series name falls back
  // to the data field column name (legacy behaviour).
  chartFirstDataLegendLabel?: string;
  chartSecondDataLegendLabel?: string;
  chartThirdDataLegendLabel?: string;
  // KOA-18057: per-series line style (solid/dashed/dotted) for the fixed first
  // three series. Empty → solid. Honoured only by line-mode charts.
  chartFirstDataLineStyle?: string;
  chartSecondDataLineStyle?: string;
  chartThirdDataLineStyle?: string;
  // KOA-18057: chart data series beyond the fixed three slots. Additive, so old
  // symbols (no array) and the mobile generators (which read only the three named
  // slots) are untouched. Series four and up render web-only.
  chartAdditionalSeries?: ChartAdditionalSeries[];
  CalendarOptions?: CalendarOptions;
  TimelineOptions?: TimelineOptions;
  MenuOptions?: MenuOptions;
  BreadcrumbOptions?: BreadcrumbOptions;
  RadialChartOptions?: RadialChartOptions;
  RadialChartLevelOptions?: RadialChartLevelOptions;
  MixedChartOptions?: MixedChartOptions;
  MixedChartLineOptions?: MixedChartLineOptions;
  MixedChartPointOptions?: MixedChartPointOptions;
  MixedChartBarOptions?: MixedChartBarOptions;

  ChatOptions?: ChatOptions;

  KanbanBoardLaneOptions?: KanbanBoardLaneOptions;
  KanbanBoardCardOptions?: KanbanBoardCardOptions;

  NodeTreeNodeOptions?: NodeTreeNodeOptions;

  valuePlaceholderText?: string;
  tableEditModeRenderer?: string;
  sqlParamName?: string;
  parentActionId?: string;

  resultActionId?: string;
}

export interface IEvent {
  eventName: string;
  actions: IAction[];
  isDynamicEvent?: boolean;
}

export interface IAction {
  // Notify
  id: string;
  label: string;
  description: string;
  actionDefinitionId: string | undefined;
  parameters: IActionParameter[]; // message
  targetScreenInputParams: ITargetScreenInputParam[];
  condition?: IConditionModel;
  confirmationMessage?: string;
  confirmationFontFamily?: string;
  confirmationHeader?: string;
  confirmationIcon?: string;
  confirmationIconBackgroundColor?: string;
  confirmationConfirmButtonColor?: string;
  confirmationCancelButtonColor?: string;
  ifThenElseData?: IIfThenElseDataModel;
  forEachData?: IForEachDataModel;
  recurringActionsData?: IRecurringActionsDataModel;
  multiReportData?: IMultiReportPrintData;
  mqttSubscribeActionData?: IMqttActionsDataModel;
  dontTriggerValidations: boolean;
  appSupportType: EAppSupportType;
  managedDbData?: IManagedDbDataModel;
  isRecurring?: boolean;
  recurringInterval?: number;
  returnType?: BackEndValueType;
  returnTypeDynamicInterface?: IKType;
  returnCardinality?: ECardinality;
  returnTypeModel?: IKType;
  tag?: string | null;
  note?: string | null;
  addGoBackAction?: boolean;
  sqlLikeSetting?: string; // SW: StartsWith | C: Contains | EW: EndsWith | N:None
  isAuditEnabled?: boolean;
  auditLevel?: string;
  enabled?: boolean;
}

export interface IManagedDbDataModel {
  save?: IManagedDbSaveModel;
  delete?: IManagedDbDeleteModel;
}

export interface IManagedDbSaveModel {
  method: EManagedDbSaveMethod;
  selectedTableName?: string;
  selectedTableSchema?: string;
  selectedTableId?: string;
  selectedDatasourceId?: string;
  parameters: IActionParameter[];
  where?: IManagedDbWhereModel[];
  idColName?: string;
}

export interface IManagedDbDeleteModel {
  selectedTableName?: string;
  selectedTableSchema?: string;
  selectedTableId?: string;
  selectedDatasourceId?: string;
  idParameter: IActionParameter;
}

export interface IManagedDbWhereModel {
  type: EManagedDbUpdateWhereType;
  andCondition?: IManagedDbWhereConditionModel;
  orConditions?: IManagedDbWhereConditionModel[];
}

export interface IManagedDbWhereConditionModel {
  colName?: string;
  conditionOperator: EManagedDbUpdateWhereConditionOperator;
  symbol: ISymbol;
  typeName: BackEndValueType | undefined;
}

export enum EManagedDbUpdateWhereType {
  and = 1,
  orGroup = 2
}

export enum EManagedDbUpdateWhereConditionOperator {
  isEqualTo = "isEqualTo",
  isNotEqualTo = "isNotEqualTo",
  startsWith = "startsWith",
  doesNotStartWith = "doesNotStartWith",
  contains = "contains",
  doesNotContain = "doesNotContain",
  endsWith = "endsWith",
  doesNotEndtWith = "doesNotEndWith",
  isLessThan = "isLessThan",
  isLessOrEqual = "isLessOrEqual",
  isGraeterThan = "isGreaterThan",
  isGreaterOrEqual = "isGreaterOrEqual"
}

export enum EManagedDbSaveMethod {
  smart = 1,
  insert = 2,
  update = 3
}

export interface IIfThenElseDataModel {
  condition: IConditionModel;
  ifActions: IAction[];
  elseActions: IAction[];
}

export interface IRecurringActionsDataModel {
  recurringActions: IAction[];
}

export interface IMqttActionsDataModel {
  mqttSubscribeActions: IAction[];
}

export interface IValidation {
  id: string;
  message: string;
  condition: IConditionModel;
  validationCondition?: IConditionModel;
  isExpandedPanel?: boolean;
  ignoreIfNotVisible?: boolean;
}

export interface IVisibility {
  id: string;
  visibilityType?: "visible" | "hidden" | "custom";
  condition?: IConditionModel;
}

export interface IConditionModel {
  field1?: ISymbol;
  conditionOperator?: string;
  field2?: ISymbol;
  conditionSet?: IConditionSet;
  id?: string;
  isOrGroup?: boolean;
}

export interface IConditionSet {
  type: ConditionSetType;
  conditions: IConditionModel[];
}

export enum ConditionSetType {
  and = 1,
  or = 2
}

export interface IActionParameter {
  // id: string;
  name: string; // message
  Typename: BackEndValueType;
  cardinality: ECardinality; // one
  symbol: ISymbol; // --fixedValue, default(username),
  parameters?: IActionParameter[];
  isEnum?: boolean;
  enumTypeValues?: string[];
  isObject?: boolean;
  isObjectCollapsed?: boolean;
  isDeletedOnSource?: boolean;
  isOptionalParameter?: boolean;
  hideFromOptionalParameters?: boolean;
  isExternalVisible?: boolean;
  displayName?: string;
  ownerPropName?: string;
  realPropName?: string;
  objectClassName?: string;
  ownerClassName?: string;
}

export interface ITargetScreenInputParam {
  name: string;
  type: string; // IKType;
  typeName: string; // IKType;
  cardinality: ECardinality;
  symbol: ISymbol;
}

export interface IForEachDataModel {
  actions: IAction[];
  symbolModel: ISymbol;
}

/**
 * Page layout for the merged output of the GetMultipleReportsBase64 action.
 * - "onePerPage": each report (and each repetition) starts on a fresh page (simple page concatenation).
 * - "packed": reports are stamped onto shared pages where they fit; a new page starts only when the
 *   next report does not fit in the remaining vertical space.
 */
export type EMultiReportLayout = "onePerPage" | "packed";

/** How a report block produces its renders: hand-authored input sets vs. one render per collection element. */
export type EMultiReportBlockMode = "static" | "dynamic";

/**
 * One concrete render of a report block: the set of report-input bindings used for this single render.
 * In static mode each instance is authored by hand; in dynamic mode instances are derived at runtime
 * from the bound collection (see IMultiReportBlock.dynamicSource).
 */
export interface IMultiReportInstance {
  id: string;
  targetScreenInputParams: ITargetScreenInputParam[];
}

/**
 * A single selected report and how many times / with which inputs it is produced.
 * The report identity (reportScreenId) is always static — the generator resolves the report type at
 * codegen time. Only the inputs and the repetition count can be dynamic.
 */
export interface IMultiReportBlock {
  id: string;
  reportScreenId: string;
  mode: EMultiReportBlockMode;
  /** Static mode: the hand-authored input sets, one per render. */
  instances: IMultiReportInstance[];
  /** Dynamic mode: a cardinality-Many collection; the report is produced once per element. */
  dynamicSource?: ISymbol;
}

/** Payload of the GetMultipleReportsBase64 action: one or more report blocks merged into a single PDF. */
export interface IMultiReportPrintData {
  layoutMode: EMultiReportLayout;
  /** Vertical gap (px) between reports in "packed" layout. Ignored for "onePerPage". */
  gap?: number;
  reports: IMultiReportBlock[];
}
